import { format } from "date-fns";
import moment from "moment";
import phone from "phone";
import { AneuploidyICDCodesClass } from "../../recoil/ICD10Codes/aneuploidyICDCodes";
import { CarrierICDCodesClass } from "../../recoil/ICD10Codes/carrierICDCodes";
import { FetalAntigenICDCodesClass } from "../../recoil/ICD10Codes/fetalAntigenICDCodes";
import { RhdICDCodesClass } from "../../recoil/ICD10Codes/rhdICDCodes";
import { Auth0UserSerializer, NewRequisitionForm } from "../../recoil/types";
import {
  formatWithTimezoneOffset,
  getNumberOfDaysDifference,
  getWeeksDiffGestation,
  parseYmdAtNoonUtc,
} from "../../utils/helpers";
import { validateEmail } from "../../utils/hooks/helpers";
import { BillingType, StepperIndex } from "./types";
import {
  getSelectedFetalAntigenPanel,
  isFetalAntigenDocumentSatisfied,
  isFetalAntigenSelectionValid,
} from "./fetalAntigenConstants";

const TEN_YEARS_IN_DAYS = 3650;

const ONE_YEAR_IN_DAYS = 365;

export const normalizeMedicalRecordNumber = (value: string): string =>
  value.trim().replace(/^(?:MRN\s*[:-]|#)\s*/i, "");

export const defaultReq = {
  firstName: "John",
  middleInitial: "",
  lastName: "Smith",
  medicalRecordNumber: "",
  patientSheetAttached: false,
  sex: "Male" as const,
  maternalWeight: 0,
  dateOfBirth: "1990-09-09",
  estimatedDueDate: moment().add(10, "days").format("yyyy-MM-DD"),
  isTwin: true,
  isTripleOrMore: false,
  isEggDonor: false,
  ageOfEggDonor: 0,
  notPregnant: false,
  cellPhone: "231",
  emailAddress: "john.smith@fake.com",
  fullAddress: "",
  streetAddress: "77 Smith St",
  aptUnit: "APT 1",
  city: "Brooklyn",
  state: "NY",
  zipCode: "11211",
  asian: true,
  african: false,
  ashkenazi: true,
  middleEastern: false,
  frenchCanadian: false,
  hispanic: false,
  northernEuropean: false,
  white: false,
  otherEthnicity: false,
  unknownEthnicity: false,
  familyHistoryGeneticDisorders: "",
  reportedCarrierPatientAlpha: true,
  reportedCarrierPatientSickle: false,
  reportedCarrierPatientSMA: false,
  reportedCarrierPatientCF: false,
  reportedCarrierPatientVariant: "",
  reportedCarrierFOBAlpha: false,
  reportedCarrierFOBSickle: false,
  reportedCarrierFOBSMA: false,
  reportedCarrierFOBCF: false,
  reportedCarrierFOBVariant: "",
  patientAcknowledgement: true,
  carrierScreen: false,
  expandedCarrierPanel: null,
  isRhdOnlyOrder: false,
  aneuploidyNIPT: true,
  aneuploidyTwinNIPT: false,
  aneuploidyOptOutFetalSex: false,
  aneuploidyIncludeRhd: false,
  isFragileX: false,
  isFetalAntigen: false,
  isFetalAntigenRbc: false,
  isFetalAntigenPlatelet: false,
  isFetalAntigenBigC: false,
  isFetalAntigenLittleC: false,
  isFetalAntigenD: false,
  isFetalAntigenDuffy: false,
  isFetalAntigenE: false,
  isFetalAntigenKell: false,
  isFetalAntigenLittleE: false,
  isFetalAntigenFyb: false,
  isFetalAntigenJka: false,
  isFetalAntigenJkb: false,
  isFetalAntigenLittleK: false,
  isFetalAntigenM: false,
  isFetalAntigenN: false,
  isFetalAntigenBigS: false,
  isFetalAntigenLittleS: false,
  isFetalAntigenU: false,
  isFetalAntigenHpa1a: false,
  isFetalAntigenHpa1b: false,
  isFetalAntigenHpa2a: false,
  isFetalAntigenHpa2b: false,
  isFetalAntigenHpa3a: false,
  isFetalAntigenHpa3b: false,
  isFetalAntigenHpa4a: false,
  isFetalAntigenHpa4b: false,
  isFetalAntigenHpa5a: false,
  isFetalAntigenHpa5b: false,
  isFetalAntigenHpa9a: false,
  isFetalAntigenHpa9b: false,
  isFetalAntigenHpa15a: false,
  isFetalAntigenHpa15b: false,
  insuranceSheetAttached: false,
  Z8481: true,
  Z31430: false,
  Z31440: false,
  Z810: false,
  O285_carrier: true,
  O285_aneuploidy: false, // here we don't check by default
  O09511: true,
  O09512: false,
  O09513: false,
  O09521: false,
  O09522: false,
  O09523: false,
  O09891: false,
  O09892: false,
  O09893: false,
  Z3481_aneuploidy: false,
  Z3482_aneuploidy: false,
  Z3483_aneuploidy: false,
  Z3401: false,
  Z3402: false,
  Z3403: false,
  Z360: false,
  O360110: true,
  O360120: false,
  O360130: false,
  Z3182: false,
  O361910: true,
  O361920: false,
  O361930: false,
  otherICDCodes: "",
  otherCarrierScreenICDCodes: "",
  otherAneuploidyICDCodes: "",
  otherRhdICDCodes: "",
  otherFetalAntigenICDCodes: "",
  orderingHealthcareProviders: "John Spivey, MD",
  orderingHealthcareNPI: "4511639670",
  orderingHealthcareProviderFormatted: "John Spivey, MD, NPI: 4511639670",
  isStandardAccount: true,
  isInternationalAccount: false,
  isMobilePhlebotomyAccount: false,
  isPatientSheetAccount: false,
  isInsuranceSheetAccount: false,
  isFetalAntigenAccount: false,
  isFragileXAccount: false,
  isRhdOnlyAccount: false,
  is130PanelAccount: false,
  is611PanelAccount: false,
  is113CustomPanelAccount: false,
  isMobilePhlebotomy: false,
  providerAuthorization: true,
  clinicName: "BillionToOne - House Account",
  clinicPhone: "5555550124",
  clinicFax: "",
  clinicAccountNumber: "1001",
  clinicAdditionalNotes: "",
  billToInsurance: false,
  billToInsuranceType: "Entry",
  insuranceCompanyName: "",
  groupId: "",
  memberId: "",
  policyOwnerFirstName: "",
  policyOwnerLastName: "",
  policyOwnerDob: "",
  relationshipToPolicyOwnerSelf: false,
  relationshipToPolicyOwnerDependent: false,
  relationshipToPolicyOwnerOther: false,
  billToPatient: true,
  billToPatientEmail: "sdadsa",
  billToClient: false,
  billToPendingMedicaid: false,
  billToCashPay: false,
  editTreatmentNotes: false,
  treatmentNotes: "",
  insuranceFileUrls: {},
  antigenFileUrls: {},
  disableAneuploidy: false,
  requireFetalAntigenFile: true,
} as NewRequisitionForm;

export const checkICDCodes = (
  newGlobalRequisition: NewRequisitionForm,
): boolean => {
  let isError = false;
  if (newGlobalRequisition.isInternationalAccount) {
    return isError;
  }
  if (newGlobalRequisition.carrierScreen) {
    isError = isError || checkCarrierICDCodes(newGlobalRequisition);
  }
  if (newGlobalRequisition.aneuploidyNIPT) {
    isError = isError || checkAneuploidyICDCodes(newGlobalRequisition);
  }
  if (
    newGlobalRequisition.aneuploidyIncludeRhd ||
    newGlobalRequisition.isRhdOnlyOrder
  ) {
    isError = isError || checkRhdICDCodes(newGlobalRequisition);
  }
  if (newGlobalRequisition.isFetalAntigen) {
    isError = isError || checkFetalAntigenICDCodes(newGlobalRequisition);
  }
  return isError;
};

export type AllCodes =
  | CarrierICDCodesClass
  | AneuploidyICDCodesClass
  | RhdICDCodesClass
  | FetalAntigenICDCodesClass;
export const checkCodesOfCodeObject = (
  codeObject: AllCodes,
  newGlobalRequisition: NewRequisitionForm,
  otherTestPanelICDCodes: string, // the specific test panel other codes
): boolean => {
  let codeSet: boolean =
    newGlobalRequisition.otherICDCodes !== "" ||
    Boolean(otherTestPanelICDCodes);
  const codes = Object.keys(codeObject);
  codes.forEach((code: string) => {
    const isAutoAddedCode = code === "O285_carrier" || code === "Z360";
    if (!isAutoAddedCode) {
      codeSet =
        codeSet ||
        Boolean(newGlobalRequisition[code as keyof typeof codeObject]);
    }
  });
  return !codeSet;
};

export const checkCarrierICDCodes = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  checkCodesOfCodeObject(
    new CarrierICDCodesClass(),
    newGlobalRequisition,
    newGlobalRequisition.otherCarrierScreenICDCodes,
  );

export const checkAneuploidyICDCodes = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  checkCodesOfCodeObject(
    new AneuploidyICDCodesClass(),
    newGlobalRequisition,
    newGlobalRequisition.otherAneuploidyICDCodes,
  );

export const checkRhdICDCodes = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  checkCodesOfCodeObject(
    new RhdICDCodesClass(),
    newGlobalRequisition,
    newGlobalRequisition.otherRhdICDCodes,
  );

export const checkFetalAntigenICDCodes = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  checkCodesOfCodeObject(
    new FetalAntigenICDCodesClass(),
    newGlobalRequisition,
    newGlobalRequisition.otherFetalAntigenICDCodes,
  );

export const checkDateValidation = (
  type: "EDD" | "DOB" | "DOBPO" | "both",
  newGlobalRequisition: NewRequisitionForm,
): boolean => {
  if (type === "EDD") {
    return (
      (Boolean(newGlobalRequisition.estimatedDueDate) &&
        new Date(newGlobalRequisition.estimatedDueDate) < new Date()) ||
      (Boolean(newGlobalRequisition.estimatedDueDate) &&
        getNumberOfDaysDifference(
          new Date(),
          new Date(newGlobalRequisition.estimatedDueDate),
        ) > ONE_YEAR_IN_DAYS)
    );
  }
  if (type === "DOB") {
    return (
      (Boolean(newGlobalRequisition.dateOfBirth) &&
        new Date(newGlobalRequisition.dateOfBirth) > new Date()) ||
      (Boolean(newGlobalRequisition.dateOfBirth) &&
        getNumberOfDaysDifference(
          new Date(newGlobalRequisition.dateOfBirth),
          new Date(),
        ) < TEN_YEARS_IN_DAYS) ||
      Boolean(
        Boolean(newGlobalRequisition.dateOfBirth) &&
        new Date(newGlobalRequisition.dateOfBirth) < new Date("1/1/1950"),
      )
    );
  }

  if (type === "DOBPO") {
    return (
      (Boolean(newGlobalRequisition.policyOwnerDob) &&
        new Date(newGlobalRequisition.policyOwnerDob) > new Date()) ||
      (Boolean(newGlobalRequisition.policyOwnerDob) &&
        getNumberOfDaysDifference(
          new Date(newGlobalRequisition.policyOwnerDob),
          new Date(),
        ) < TEN_YEARS_IN_DAYS) ||
      Boolean(
        Boolean(newGlobalRequisition.policyOwnerDob) &&
        new Date(newGlobalRequisition.policyOwnerDob) < new Date("1/1/1950"),
      )
    );
  }
  return (
    (Boolean(newGlobalRequisition.dateOfBirth) &&
      new Date(newGlobalRequisition.dateOfBirth) > new Date()) ||
    (Boolean(newGlobalRequisition.dateOfBirth) &&
      getNumberOfDaysDifference(
        new Date(newGlobalRequisition.dateOfBirth),
        new Date(),
      ) < TEN_YEARS_IN_DAYS) ||
    Boolean(
      Boolean(newGlobalRequisition.dateOfBirth) &&
      new Date(newGlobalRequisition.dateOfBirth) < new Date("1/1/1950"),
    ) ||
    (Boolean(newGlobalRequisition.estimatedDueDate) &&
      new Date(newGlobalRequisition.estimatedDueDate) < new Date()) ||
    (Boolean(newGlobalRequisition.estimatedDueDate) &&
      getNumberOfDaysDifference(
        new Date(),
        new Date(newGlobalRequisition.estimatedDueDate),
      ) > ONE_YEAR_IN_DAYS)
  );
};

export const checkDateValidationError = (
  type: "EDD" | "DOB" | "DOBPO" | "both",
  newGlobalRequisition: NewRequisitionForm,
): string => {
  if (type === "DOB") {
    if (!newGlobalRequisition.dateOfBirth) {
      return "Enter DOB";
    }
    if (
      Boolean(newGlobalRequisition.dateOfBirth) &&
      new Date(newGlobalRequisition.dateOfBirth) < new Date("1/1/1950")
    ) {
      return "Date of Birth should be more recent.";
    }
    if (
      Boolean(newGlobalRequisition.dateOfBirth) &&
      new Date(newGlobalRequisition.dateOfBirth) > new Date()
    ) {
      return "Date of Birth should be in the past.";
    }
    if (
      Boolean(newGlobalRequisition.dateOfBirth) &&
      getNumberOfDaysDifference(
        new Date(newGlobalRequisition.dateOfBirth),
        new Date(),
      ) < TEN_YEARS_IN_DAYS
    ) {
      return "Date of Birth should be sufficiently in the past.";
    }
  }
  if (type === "DOBPO") {
    if (!newGlobalRequisition.policyOwnerDob) {
      return "Enter DOB";
    }
    if (
      Boolean(newGlobalRequisition.policyOwnerDob) &&
      new Date(newGlobalRequisition.policyOwnerDob) < new Date("1/1/1950")
    ) {
      return "Date of Birth should be more recent.";
    }
    if (
      Boolean(newGlobalRequisition.policyOwnerDob) &&
      new Date(newGlobalRequisition.policyOwnerDob) > new Date()
    ) {
      return "Date of Birth should be in the past.";
    }
    if (
      Boolean(newGlobalRequisition.policyOwnerDob) &&
      getNumberOfDaysDifference(
        new Date(newGlobalRequisition.policyOwnerDob),
        new Date(),
      ) < TEN_YEARS_IN_DAYS
    ) {
      return "Date of Birth should be sufficiently in the past.";
    }
  }
  if (type === "EDD") {
    if (!newGlobalRequisition.estimatedDueDate) {
      return "Enter EDD";
    }
    if (
      Boolean(newGlobalRequisition.estimatedDueDate) &&
      new Date(newGlobalRequisition.estimatedDueDate) < new Date()
    ) {
      return "Estimated Due Date should be in the future.";
    }
    if (
      Boolean(newGlobalRequisition.estimatedDueDate) &&
      getNumberOfDaysDifference(
        new Date(),
        new Date(newGlobalRequisition.estimatedDueDate),
      ) > ONE_YEAR_IN_DAYS
    ) {
      return "Estimated Due Date should not be past one year from today.";
    }
  }
  return "";
};

export const checkPhoneAndEmail = (
  type: "phone" | "email",
  newGlobalRequisition: NewRequisitionForm,
): boolean => {
  if (type === "phone") {
    return (
      (!newGlobalRequisition.isInternationalAccount &&
        !newGlobalRequisition.cellPhone) ||
      (newGlobalRequisition.cellPhone.length > 4 &&
        !phone(newGlobalRequisition.cellPhone).isValid)
    );
  }
  if (type === "email" && newGlobalRequisition.emailAddress) {
    return !validateEmail(newGlobalRequisition.emailAddress);
  }
  return false;
};

export const requiresEstimatedDueDate = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  newGlobalRequisition.sex !== "Male" && !newGlobalRequisition.notPregnant;

export const isAneuploidyIneligible = (form: NewRequisitionForm): boolean =>
  !requiresEstimatedDueDate(form) ||
  form.isTripleOrMore ||
  (form.isTwin && form.isEggDonor);

export const getPatientSummaryPregnancyLabel = (
  form: NewRequisitionForm,
): string => {
  if (form.sex === "Male" || form.notPregnant) {
    return "Not pregnant";
  }
  if (form.isTripleOrMore) {
    return "Triplets or higher";
  }
  if (form.isTwin) {
    return "Twin";
  }
  if (form.isEggDonor) {
    return "Singleton + Egg donor";
  }
  return "Singleton";
};

export const getPatientPregnancyNormalizationUpdates = (
  newGlobalRequisition: NewRequisitionForm,
): Partial<NewRequisitionForm> => {
  const updates: Partial<NewRequisitionForm> = {};
  if (!requiresEstimatedDueDate(newGlobalRequisition)) {
    if (newGlobalRequisition.estimatedDueDate) updates.estimatedDueDate = "";
    if (newGlobalRequisition.isEggDonor) updates.isEggDonor = false;
    if (newGlobalRequisition.ageOfEggDonor) updates.ageOfEggDonor = 0;
    if (newGlobalRequisition.isTwin) updates.isTwin = false;
    if (newGlobalRequisition.isTripleOrMore) updates.isTripleOrMore = false;
  }
  return updates;
};

export const normalizePatientPregnancyFields = (
  newGlobalRequisition: NewRequisitionForm,
): NewRequisitionForm => ({
  ...newGlobalRequisition,
  ...getPatientPregnancyNormalizationUpdates(newGlobalRequisition),
});

export type RequisitionValidationErrorKey =
  | "clinic.accountNumber"
  | "clinic.name"
  | "clinic.provider"
  | "clinic.providerNpi"
  | "patient.reproductivePartnerDateOfBirth"
  | "patient.reproductivePartnerFirstName"
  | "patient.reproductivePartnerLastName"
  | "patient.firstName"
  | "patient.lastName"
  | "patient.dateOfBirth"
  | "patient.estimatedDueDate"
  | "patient.sex"
  | "patient.cellPhone"
  | "patient.emailAddress"
  | "clinicalIndication.testPanel"
  | "clinicalIndication.providerAuthorization"
  | "clinicalIndication.icdCodes"
  | "clinicalIndication.fetalAntigenFile"
  | "clinicalIndication.fetalAntigenSelection"
  | "billing.insuranceType"
  | "billing.insuranceCompanyName"
  | "billing.groupId"
  | "billing.memberId"
  | "billing.policyOwnerFirstName"
  | "billing.policyOwnerLastName"
  | "billing.policyOwnerDob"
  | "billing.relationshipToPolicyOwner"
  | "billing.insuranceFile"
  | "billing.treatmentNotes"
  | "billing.emailAddress"
  | "billing.type";

export type RequisitionValidationErrors = Partial<
  Record<RequisitionValidationErrorKey, true>
>;

const compactValidationErrors = (
  validationErrors: Partial<Record<RequisitionValidationErrorKey, boolean>>,
): RequisitionValidationErrors =>
  Object.fromEntries(
    Object.entries(validationErrors).filter(([, hasError]) => hasError),
  ) as RequisitionValidationErrors;

export const hasRequisitionValidationErrors = (
  validationErrors: RequisitionValidationErrors,
): boolean => Object.keys(validationErrors).length > 0;

export const getClinicValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors =>
  compactValidationErrors({
    "clinic.accountNumber": !newGlobalRequisition.clinicAccountNumber,
    "clinic.name": !newGlobalRequisition.clinicName,
    "clinic.provider": !newGlobalRequisition.orderingHealthcareProviders,
    "clinic.providerNpi": !newGlobalRequisition.orderingHealthcareNPI,
  });

export const hasClinicValidationError = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  hasRequisitionValidationErrors(
    getClinicValidationErrors(newGlobalRequisition),
  );

export const getPatientValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors => {
  const hasReproductivePartner =
    newGlobalRequisition.maternalReproductivePartner ||
    newGlobalRequisition.paternalReproductivePartner;

  return compactValidationErrors({
    "patient.reproductivePartnerDateOfBirth": Boolean(
      hasReproductivePartner &&
      !newGlobalRequisition.reproductivePartnerDateOfBirth,
    ),
    "patient.reproductivePartnerFirstName": Boolean(
      hasReproductivePartner &&
      !newGlobalRequisition.reproductivePartnerFirstName,
    ),
    "patient.reproductivePartnerLastName": Boolean(
      hasReproductivePartner &&
      !newGlobalRequisition.reproductivePartnerLastName,
    ),
    "patient.firstName": !newGlobalRequisition.firstName,
    "patient.lastName": !newGlobalRequisition.lastName,
    "patient.dateOfBirth":
      !newGlobalRequisition.dateOfBirth ||
      checkDateValidation("DOB", newGlobalRequisition),
    "patient.estimatedDueDate": Boolean(
      (!newGlobalRequisition.notPregnant &&
        newGlobalRequisition.sex !== "Male" &&
        !newGlobalRequisition.estimatedDueDate) ||
      (requiresEstimatedDueDate(newGlobalRequisition) &&
        checkDateValidation("EDD", newGlobalRequisition)),
    ),
    "patient.sex": !newGlobalRequisition.sex,
    "patient.cellPhone": checkPhoneAndEmail("phone", newGlobalRequisition),
    "patient.emailAddress": checkPhoneAndEmail("email", newGlobalRequisition),
  });
};

export const hasPatientValidationError = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  hasRequisitionValidationErrors(
    getPatientValidationErrors(newGlobalRequisition),
  );

export const getClinicalIndicationValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors =>
  compactValidationErrors({
    "clinicalIndication.testPanel":
      !newGlobalRequisition.aneuploidyNIPT &&
      !newGlobalRequisition.carrierScreen &&
      !newGlobalRequisition.isRhdOnlyOrder,
    "clinicalIndication.providerAuthorization":
      !newGlobalRequisition.providerAuthorization,
    "clinicalIndication.icdCodes": checkICDCodes(newGlobalRequisition),
    "clinicalIndication.fetalAntigenFile": Boolean(
      newGlobalRequisition.isFetalAntigen &&
      !isFetalAntigenDocumentSatisfied(
        newGlobalRequisition.requireFetalAntigenFile,
        newGlobalRequisition.antigenFileUrls,
      ),
    ),
    "clinicalIndication.fetalAntigenSelection": Boolean(
      newGlobalRequisition.isFetalAntigen &&
      !isFetalAntigenSelectionValid(
        getSelectedFetalAntigenPanel(newGlobalRequisition),
        newGlobalRequisition.isFetalAntigenRbc,
        newGlobalRequisition.isFetalAntigenPlatelet,
      ),
    ),
  });

export const hasClinicalIndicationValidationError = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  hasRequisitionValidationErrors(
    getClinicalIndicationValidationErrors(newGlobalRequisition),
  );

export const getBillingValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors => {
  if (newGlobalRequisition.billToInsurance) {
    if (!newGlobalRequisition.billToInsuranceType) {
      return { "billing.insuranceType": true };
    }
    if (newGlobalRequisition.billToInsuranceType === "Entry") {
      return compactValidationErrors({
        "billing.insuranceCompanyName":
          !newGlobalRequisition.insuranceCompanyName,
        "billing.groupId": !newGlobalRequisition.groupId,
        "billing.memberId": !newGlobalRequisition.memberId,
        "billing.policyOwnerFirstName":
          !newGlobalRequisition.policyOwnerFirstName,
        "billing.policyOwnerLastName":
          !newGlobalRequisition.policyOwnerLastName,
        "billing.policyOwnerDob":
          !newGlobalRequisition.policyOwnerDob ||
          checkDateValidation("DOBPO", newGlobalRequisition),
        "billing.relationshipToPolicyOwner":
          !newGlobalRequisition.relationshipToPolicyOwnerDependent &&
          !newGlobalRequisition.relationshipToPolicyOwnerSelf &&
          !newGlobalRequisition.relationshipToPolicyOwnerOther,
      });
    }
    if (newGlobalRequisition.billToInsuranceType === "Upload") {
      return compactValidationErrors({
        "billing.insuranceFile": !Object.keys(
          newGlobalRequisition.insuranceFileUrls,
        ).length,
        "billing.relationshipToPolicyOwner":
          !newGlobalRequisition.relationshipToPolicyOwnerDependent &&
          !newGlobalRequisition.relationshipToPolicyOwnerSelf &&
          !newGlobalRequisition.relationshipToPolicyOwnerOther,
      });
    }
    if (newGlobalRequisition.billToInsuranceType !== "Attach") {
      return { "billing.insuranceType": true };
    }
    return compactValidationErrors({
      "billing.treatmentNotes": !newGlobalRequisition.treatmentNotes,
    });
  }
  if (newGlobalRequisition.billToPatient) {
    return compactValidationErrors({
      "billing.emailAddress":
        !newGlobalRequisition.emailAddress ||
        checkPhoneAndEmail("email", newGlobalRequisition),
    });
  }
  if (
    newGlobalRequisition.billToInsurance ||
    newGlobalRequisition.billToPatient ||
    newGlobalRequisition.billToClient ||
    newGlobalRequisition.billToPendingMedicaid
  ) {
    return {};
  }
  return { "billing.type": true };
};

export const hasBillingValidationError = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  hasRequisitionValidationErrors(
    getBillingValidationErrors(newGlobalRequisition),
  );

export const getOrderSubmitValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
  options: { includeClinic?: boolean } = {},
): RequisitionValidationErrors => {
  const { includeClinic = true } = options;
  return {
    ...(includeClinic ? getClinicValidationErrors(newGlobalRequisition) : {}),
    ...getPatientValidationErrors(newGlobalRequisition),
    ...getClinicalIndicationValidationErrors(newGlobalRequisition),
    ...getBillingValidationErrors(newGlobalRequisition),
  };
};

export const hasOrderSubmitValidationError = (
  newGlobalRequisition: NewRequisitionForm,
  options: { includeClinic?: boolean } = {},
): boolean =>
  hasRequisitionValidationErrors(
    getOrderSubmitValidationErrors(newGlobalRequisition, options),
  );

export const getEditOrderValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors => ({
  ...getClinicValidationErrors(newGlobalRequisition),
  ...getPatientValidationErrors(newGlobalRequisition),
});

export const hasEditOrderValidationError = (
  newGlobalRequisition: NewRequisitionForm,
): boolean =>
  hasRequisitionValidationErrors(
    getEditOrderValidationErrors(newGlobalRequisition),
  );

export const getDraftValidationErrors = (
  newGlobalRequisition: NewRequisitionForm,
): RequisitionValidationErrors =>
  compactValidationErrors({
    "clinic.accountNumber": !newGlobalRequisition.clinicAccountNumber,
    "clinic.name": !newGlobalRequisition.clinicName,
    "clinic.providerNpi": !newGlobalRequisition.orderingHealthcareNPI,
    "clinic.provider": !newGlobalRequisition.orderingHealthcareProviders,
    "patient.firstName": !newGlobalRequisition.firstName,
    "patient.lastName": !newGlobalRequisition.lastName,
    "patient.dateOfBirth": !newGlobalRequisition.dateOfBirth,
  });

export const getNextValidationDisabled = (
  newGlobalRequisition: NewRequisitionForm,
  activeStep: number,
): boolean | undefined => {
  // Return true if there is a validation error
  if (activeStep === StepperIndex.Clinic) {
    return hasClinicValidationError(newGlobalRequisition);
  }
  if (activeStep === StepperIndex.Patient) {
    return hasPatientValidationError(newGlobalRequisition);
  }

  if (activeStep === StepperIndex.Indication) {
    return hasClinicalIndicationValidationError(newGlobalRequisition);
  }
  if (activeStep === StepperIndex.Billing) {
    return hasBillingValidationError(newGlobalRequisition);
  }
  if (activeStep === StepperIndex.Shipping) {
    return false;
  }
};

export const getDraftNextValidationDisabled = (
  newGlobalRequisition: NewRequisitionForm,
): boolean | undefined =>
  hasRequisitionValidationErrors(
    getDraftValidationErrors(newGlobalRequisition),
  );

export const getRelationshipToInsured = (
  self?: boolean,
  dependent?: boolean,
  other?: boolean,
): "Self" | "Dependent" | "Other" | "" => {
  if (self) {
    return "Self";
  }
  if (dependent) {
    return "Dependent";
  }
  if (other) {
    return "Other";
  }
  return "";
};

export const getBillingType = (
  insurance?: boolean,
  client?: boolean,
  patient?: boolean,
  pendingMedicaid?: boolean,
  cashPay?: boolean,
): BillingType | "" => {
  if (insurance) {
    return BillingType.Insurance;
  }
  if (client) {
    return BillingType.Client;
  }
  if (patient) {
    return BillingType.Patient;
  }
  if (pendingMedicaid) {
    return BillingType.PendingMedicaid;
  }
  if (cashPay) {
    return BillingType.CashPay;
  }
  return "";
};

export const getBillingParameter = (
  billingType?: BillingType,
):
  | "billToInsurance"
  | "billToClient"
  | "billToPatient"
  | "billToPendingMedicaid"
  | "billToCashPay"
  | "" => {
  if (billingType === BillingType.Insurance) {
    return "billToInsurance";
  }
  if (billingType === BillingType.Client) {
    return "billToClient";
  }
  if (billingType === BillingType.Patient) {
    return "billToPatient";
  }
  if (billingType === BillingType.PendingMedicaid) {
    return "billToPendingMedicaid";
  }
  if (billingType === BillingType.CashPay) {
    return "billToCashPay";
  }
  return "";
};

// Selecting a clinic resets the mutually-exclusive bill-to state so it never
// carries stale values: all bill-to flags + insurance details are cleared, and
// international accounts (which always bill to client) get billToClient set
// back on (SWE-5074).
export const getInternationalBillToReset = (
  isInternationalAccount: boolean,
): Partial<NewRequisitionForm> => ({
  billToInsurance: false,
  billToPatient: false,
  billToClient: isInternationalAccount,
  billToPendingMedicaid: false,
  billToCashPay: false,
  billToInsuranceType: "Entry",
  insuranceCompanyName: "",
  groupId: "",
  memberId: "",
  policyOwnerFirstName: "",
  policyOwnerLastName: "",
  policyOwnerDob: "",
});

export const getInsuredParameter = (
  insured?: "Self" | "Dependent" | "Other",
):
  | "relationshipToPolicyOwnerSelf"
  | "relationshipToPolicyOwnerDependent"
  | "relationshipToPolicyOwnerOther"
  | "" => {
  if (insured === "Self") {
    return "relationshipToPolicyOwnerSelf";
  }
  if (insured === "Dependent") {
    return "relationshipToPolicyOwnerDependent";
  }
  if (insured === "Other") {
    return "relationshipToPolicyOwnerOther";
  }
  return "";
};

export const getNewReqTreatmentScreenNotes = (
  newReq: NewRequisitionForm,
): string => {
  const collectionDate = newReq.collectionDate
    ? parseYmdAtNoonUtc(newReq.collectionDate)
    : new Date();
  if (newReq.isRhdOnlyOrder) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. We discussed genetic screening options for this pregnancy, including cell-free DNA testing for fetal RhD status, which is supported by ACOG for prioritizing antepartum administration of RhIg in RhD-negative pregnancies. The patient was counseled on the potential results, benefits, and limitations of the screen.

The patient was referred to BillionToOne for the Unity RhD NIPT, which assesses fetal status for RhD for pregnant patients who are RhD-negative.`;
  }
  if (newReq.carrierScreen && !newReq.aneuploidyNIPT) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. The patient was referred to BillionToOne for the Unity™ Screen Test. BillionToOne is the only lab that can do direct fetal risk assessment for ACOG-recommended recessive genetic conditions without testing the father of the pregnancy and is, therefore, medically necessary for this patient.

I’m writing to inform you that my patient${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? `, who is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ""
    } has not had sufficient genetic carrier screening to my knowledge. They have been counseled on the potential results, benefits, and limitations of genetic carrier screening, and has decided to proceed. 

Importantly, your network of laboratories does not include an in-state or out-of-state laboratory that can provide informative screening for recessive conditions for this pregnancy without subsequent paternal testing and/or invasive procedures for the pregnant patient.`;
  }
  if (
    !newReq.carrierScreen &&
    newReq.aneuploidyNIPT &&
    !newReq.aneuploidyIncludeRhd
  ) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. We discussed genetic screening options for this pregnancy, including Aneuploidy screening per ACOG guidelines, which supports NIPT for all pregnant patients regardless of maternal age or family history. The patient was counseled on the potential results, benefits, and limitations of the screen.

The patient was referred to BillionToOne for the Unity Screen Test, which assesses fetal risk for ACOG-recommended chromosome abnormalities via cell-free DNA.`;
  }
  if (
    newReq.carrierScreen &&
    newReq.aneuploidyNIPT &&
    !newReq.aneuploidyIncludeRhd
  ) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. The patient was referred to BillionToOne for the Unity™ Screen Test. BillionToOne is the only lab that can do direct fetal assessment for ACOG-recommended recessive genetic conditions without testing the father of the pregnancy and is, therefore, medically necessary for this patient.

I’m writing to inform you that my patient${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? `, who is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ""
    } has not had sufficient genetic carrier screening to my knowledge. They have been counseled on the potential results, benefits, and limitations of genetic carrier screening and aneuploidy screening and has decided to proceed.

Importantly, your network of laboratories does not include an in-state or out-of-state laboratory that can provide informative screening for recessive conditions for this pregnancy without subsequent paternal testing and/or invasive procedures for the pregnant patient.  `;
  }
  if (
    !newReq.carrierScreen &&
    newReq.aneuploidyNIPT &&
    newReq.aneuploidyIncludeRhd
  ) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. The patient is RhD negative, and we discussed genetic screening options for this pregnancy, including Aneuploidy screening per ACOG guidelines and NIPT for fetal RhD status. The patient was counseled on the potential results, benefits, and limitations of the screen.

The patient was referred to BillionToOne for the Unity Screen Test because your network of laboratories does not include an in-state or out-of-state laboratory that can provide direct fetal assessment for RhD status and assess fetal risk for ACOG recommended chromosome abnormalities via cellfree DNA. BillionToOne is the only lab in the US that can do NIPT for RhD status and fetal risk for recommended chromosome abnormalities via cell-free DNA and is, therefore, medically necessary for this patient.`;
  }
  if (
    newReq.carrierScreen &&
    newReq.aneuploidyNIPT &&
    newReq.aneuploidyIncludeRhd
  ) {
    return `${newReq.firstName} ${
      newReq.lastName
    }, born on ${formatWithTimezoneOffset(newReq.dateOfBirth)}${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? ` and is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ","
    } was seen on ${format(
      collectionDate,
      "MM/dd/yyyy",
    )}. The patient was referred to BillionToOne for the Unity™ Screen Test. BillionToOne is the only lab in the United States that can do NIPT for fetal RhD status, fetal risk for recommended chromosome abnormalities via cell-free DNA, and screening for recessive conditions for this pregnancy without testing the father of the pregnancy and is, therefore, medically necessary for this patient.

I’m writing to inform you that my patient${
      newReq.estimatedDueDate && !newReq.notPregnant
        ? `, who is ${getWeeksDiffGestation(
            parseYmdAtNoonUtc(newReq.estimatedDueDate!),
            collectionDate,
          )} weeks pregnant,`
        : ""
    } has not had sufficient genetic carrier screening to my knowledge. They have been counseled on the potential results, benefits, and limitations of genetic carrier screening, aneuploidy screening, and fetal RhD and has decided to proceed.

Importantly, your network of laboratories does not include an in-state, or out-of-state laboratory that can provide screening for recessive conditions and fetal RhD risk for this pregnancy without subsequent paternal testing and/or invasive procedures for the pregnant patient.  `;
  }
  return `${newReq.firstName} ${newReq.lastName}, born on ${formatWithTimezoneOffset(
    newReq.dateOfBirth,
  )}${
    newReq.estimatedDueDate && !newReq.notPregnant
      ? ` and is ${getWeeksDiffGestation(
          parseYmdAtNoonUtc(newReq.estimatedDueDate!),
          collectionDate,
        )} weeks pregnant,`
      : ","
  } was seen on ${format(
    collectionDate,
    "MM/dd/yyyy",
  )}. We discussed genetic screening options for this pregnancy including Carrier screening per ACOG guidelines. The patient was counseled on the potential results, benefits and limitations of the screen.

Patient was referred to BillionToOne for the Unity Test as it is the only lab that can do direct fetal assessment for recessive conditions without a paternal sample.`;
};

export const getBillToClientText = (auth: Auth0UserSerializer | undefined) => {
  if (auth?.is_vanderbilt_user) {
    return "Vanderbilt Bundle";
  }
  return "Bill to Client";
};
